import { Component, OnDestroy, OnInit, inject, ChangeDetectionStrategy, HostBinding } from '@angular/core';
import { ActivatedRoute, Router, NavigationEnd, RouterLink } from '@angular/router';
import { FormArray, FormGroup, FormControl, FormBuilder, FormsModule, ReactiveFormsModule } from '@angular/forms';

import { BehaviorSubject, filter, Subscription, take, tap } from 'rxjs';

import { TzDatePipe } from '@app/shared/tz-date.pipe';

import { AlertService } from '@app/shared/alert.service';
import { AlertAction } from '@app/shared/_models/alert';
import { ValuesService } from '@app/common/values/values.service';
import { DelrunsService } from '@app/logistic/delruns/delruns.service';
import { SpinnerService } from '@app/shared/spinner.service';
import { QuotationService } from '@app/sales/quotation/quotation.service';
import { LogisticService } from '@app/logistic/logistic.service';
import { PartnerService } from '@app/common/partner/partner.service';
import { C_value, Partner } from '@app/shared/_models/common';
import { GeoService } from '@app/shared/geo.service';
import { MiniMapComponent } from '@app/shared/mini-map/mini-map.component';
import { ImageCarouselComponent } from '@app/shared/attach/image-carousel/image-carousel.component';

import { PlanificationdeliveryComponent } from '@app/logistic/delivery/planificationdelivery/planificationdelivery.component';
import { MenuCommonComponent } from '@app/shared/menu-common/menu-common.component';
import { DeliveryValidationComponent } from '@app/logistic/delivery/delivery-validation/delivery-validation.component';
import { RentalDirectionComponent, rentalDirection } from '@app/shared/rental-direction/rental-direction.component';
import { DeliveryPrintService } from '@app/logistic/delivery/delivery-print.service';
import { DeliveryGeo } from '@app/logistic/delivery/delivery-geo';
import { Sales_order, Sales_order_l, Delivery, DeliveryL, lineNetTaxx, roundHalfAway, vatTotals } from '@app/shared/_models/sales';

import { MatDialog, MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';

import * as constants from "@app/shared/constants";
import { SalesOrderState } from '@app/shared/constants-server';
import { orderLineNamed, rentalSubtitle, rentalTitle } from '@app/sales/rental-billing/rental-billing.labels';
import Swal from 'sweetalert2';

// Miroir de SalesOrderState::is_sealed (mercalink-server common/src/dao.rs) : le serveur y refuse tout nouveau BL.
const SEALED_STATES: ReadonlySet<string> = new Set([SalesOrderState.INVOICED, SalesOrderState.PAID, SalesOrderState.CANCELED, SalesOrderState.MIGRATION]);

class HashElement {
	undelivered_qty: number = 0;
	qties: number[] = [];
}

@Component({
	selector: 'app-delivery',
	// app-image-carousel est rendu ICI une SECONDE fois : la bande de vignettes doit défiler
	// avec la carte, donc être sa sœur dans la colonne de droite, alors que celle
	// d'app-planificationdelivery vit dans la colonne de gauche. Elle est alimentée par une
	// référence de gabarit (#plan) sur ce composant ; celle de gauche est masquée par le SCSS.
	imports: [MenuCommonComponent, RouterLink,  FormsModule, ReactiveFormsModule, MatButtonModule, MatTooltipModule, MatIcon, TzDatePipe, PlanificationdeliveryComponent, MiniMapComponent, ImageCarouselComponent, RentalDirectionComponent],
	templateUrl: './delivery.component.html',
	styleUrl: './delivery.component.scss',
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: true,
})
export class DeliveryComponent implements OnInit, OnDestroy {
	private route = inject(ActivatedRoute);
	private router = inject(Router);
	private alertService = inject(AlertService);
	private spin = inject(SpinnerService);
	public dialog = inject(MatDialog);
	private fb = inject(FormBuilder);
	private quotationService = inject(QuotationService);
	private logisticService = inject(LogisticService);
	public dialogRef = inject<MatDialogRef<DeliveryComponent>>(MatDialogRef, { optional: true });
	public dialogData: any = inject(MAT_DIALOG_DATA, { optional: true });
	private valuesService = inject(ValuesService);
	private deliverySlots: C_value[] = [];	// c_values 8 : rattache un horaire (10h…) à sa demi-journée pour le lien vers le planning

	public id: number = 0;	// This is an order id
	public order: Sales_order = new Sales_order;
	public formGroup: FormGroup = new FormGroup({});
	public qtiesmap?: Map<number, HashElement> = undefined;	// Map of order_l.id -> hashelement (undelivered qty & array of qties indexed by delivery_index)
	public isQtyValidable: boolean = true;	// false = la dernière qté saisie est en erreur (l'input affiche une valeur refusée ≠ modèle)
	public delivery_index: number = -1;		// Active delivery = index of lnk_deliveries array
	public param_index: number = 0;			// Asked delivery index (query param: ?index=0)
	private opening: boolean = true;		// 1er chargement de CETTE commande, sans index demandé : l'index n'est qu'un défaut
	private explicitIndex: boolean = false;	// l'appelant a demandé un bon précis (bon cliqué, ?index=) : ni rebond ni BL du reste créé d'office
	public isSaving: boolean = false;		// Guard against double submit
	public changeWithoutSave: boolean = false;	// Modifs non sauvegardées (en attente d'un futur guard de navigation)
	/** Indique si une livraison immédiate a été validée avec succès durant cette session du dialogue. */
	public immediateDeliveryIssued: boolean = false;
	private partnerService = inject(PartnerService);
	private printer = inject(DeliveryPrintService);
	public geo = new DeliveryGeo(inject(GeoService), this.partnerService, this.spin, this.alertService);	// carte de l'adresse de livraison (bloc partagé)
	public loadOrderEventsSubject: BehaviorSubject<FormGroup> = new BehaviorSubject<FormGroup>(this.formGroup);
	private subscriptions: Subscription = new Subscription();

	get isDialog(): boolean { return !!this.dialogData; }
	@HostBinding('class.in-dialog') get hostInDialog(): boolean { return this.isDialog; }

	ngOnInit(): void {
		this.subscriptions.add(this.valuesService.getValues$(constants.C_VALUES_DELIVERY_SLOT).pipe(take(1))
			.subscribe({ next: (slots: C_value[]) => this.deliverySlots = slots ?? [], error: () => this.deliverySlots = [] }));	// sans eux : lien sur la journée entière
		if (this.isDialog) {						// Opened as a dialog from the quotation page
			this.id = this.dialogData.id;
			this.param_index = this.dialogData.index ?? 0;
			this.explicitIndex = this.dialogData.index != null;
			this.opening = !this.explicitIndex;
			this.loadOrder();
			return;									// No router-driven reloads in dialog mode
		}
		this.getUrlParams();
		this.loadOrder();

		this.subscriptions.add(
			this.router.events.pipe(
				filter(event => event instanceof NavigationEnd && this.id !== 0),
				tap(() => {
					this.getUrlParams();
					this.loadOrder();
				})
			).subscribe()
		);
	}

	getUrlParams() {
		const id = isNaN(Number(this.route.snapshot.paramMap.get('id'))) ? 0 : Number(this.route.snapshot.paramMap.get('id'));
		if (id !== this.id) {	// autre commande : on la (r)ouvre ; seul un ?index= présent est une demande explicite
			this.explicitIndex = this.route.snapshot.queryParamMap.has('index');
			this.opening = !this.explicitIndex;
		}
		this.id = id;
		this.param_index = Number(this.route.snapshot.queryParams['index']) || 0;
	}

	loadOrder() {
		this.subscriptions.add(
			this.quotationService.getQuotationById(this.id, '?o=deliveries,attachs')
				.pipe(this.spin.block('Chargement des livraisons…'))
				.subscribe({
					next: (orders: any[]) => {
						if (!orders.length) {
							this.alertService.error("ERREUR commande inexistante");
							return;
						}
						this.order = orders[0];
						this.changeWithoutSave = false;	// Données fraîches = aucune modif non sauvegardée
						this.isQtyValidable = true;		// Inputs re-rendus depuis le modèle : plus de saisie en erreur affichée
						this.qtiesmap = this.get_qtiesmap();
						this.loadBeneficiary();
						this.initGeo();		// carte en tête : coordonnées en base sinon géocodage BAN automatique
						// Aucune livraison (null OU tableau vide) : on en crée une d'office. Le module n'est atteignable
						// qu'en état PREPARATION => l'INSERT au chargement est voulu (le BL est le support de saisie des qtés).
						if (!this.order.lnk_deliveries?.length) { this.onAddDelivery(true); return; }
						// Tous les bons signés et un reste : bon suivant pré-rempli par le serveur, sauf bon précis demandé ou pièce scellée.
						if (this.allDeliveriesSigned && this.remainingQty > 0 && !this.explicitIndex && !SEALED_STATES.has(constants.getStateEnum(this.order.state) ?? '')) { this.onAddDelivery(true); return; }
						this.loadDeliveryIndex();
					},
					error: (_error) => { this.alertService.error("ERREUR au chargement des livraisons"); }
				})
		);
	}

	private reload(index: number): void {	// Reload the delivery: navigate (routed) or re-fetch in place (dialog)
		if (this.isDialog) {
			this.param_index = index;
			this.loadOrder();
		} else {
			this.spin.show();	// relais tenu : le loadOrder de l'écran rechargé rallume l'overlay avant ce hide
			this.router.navigate(['/logistic/delivery', this.id], { onSameUrlNavigation: 'reload', queryParams: { index } }).finally(() => this.spin.hide());
		}
	}

	onChangeIndex(index: number) {	// Delivery button click => save current then switch to new index
		this.onSubmit(index);		// la garde ensureQtiesValid() est portée par onSubmit (feedback au lieu d'un blocage muet)
	}

	// « Livrer maintenant les produits » : mal compris en magasin, il est d'abord présenté comme ce qu'il est, un emporté magasin.
	confirmTakeAway() {
		if (!this.ensureQtiesValid()) return;
		if (this.isSaving) return;
		Swal.fire({
			title: 'Attention !',
			html: `Ce bouton <b>ne livre pas</b> le client et <b>ne programme aucune livraison</b>.<br><br>`
				+ `Il enregistre un <b>emporté magasin</b> : le client repart <b>maintenant</b> avec <b>tous les produits</b> `
				+ `de la colonne «&nbsp;Liv.&nbsp;${this.delivery_index + 1}&nbsp;», puis il signe le bon de livraison.<br><br>`
				+ `Pour une livraison chez le client, cliquez sur «&nbsp;Annuler&nbsp;» puis sur «&nbsp;Planifier la livraison&nbsp;».`,
			icon: 'warning',
			showCancelButton: true,
			confirmButtonText: "Oui, le client emporte les produits maintenant, il ne s'agit pas d'une livraison, mais d'un emporté magasin",
			cancelButtonText: 'Annuler',
			willOpen: (popup) => liftToTopLayer(popup.parentElement),
		}).then((result) => {
			if (result.isConfirmed) this.openDeliveryValidationDialog();
		});
	}

	openDeliveryValidationDialog() {
		if (!this.ensureQtiesValid()) return;
		if (this.isSaving) return;
		// L'event 702 (DELIVERY_SIGN) ne resync que les lignes + realdatdel : une modif d'entête (mode/date/créneau)
		// non sauvegardée serait perdue au reload (le BL signé figerait une planif différente de l'écran).
		// On sauvegarde donc l'entête via UPDATE AVANT d'ouvrir le dialog de signature.
		this.isSaving = true;
		const saveFg = this.getFormGroupDelivery();
		this.subscriptions.add(
			this.logisticService.postDeliveryEvent(constants.EVENTTYP_UPDATE, saveFg).pipe(this.spin.block('Enregistrement de la livraison…')).subscribe({
				next: () => {
					this.isSaving = false;
					this.changeWithoutSave = false;
					this.saveCoordsIfDirty(() => this.openValidationDialogNow());
				},
				error: (e) => {
					this.isSaving = false;
					this.alertService.error("ERREUR Serveur. La livraison n'a pas été enregistrée");
					console.error(e.error);
				}
			})
		);
	}

	private openValidationDialogNow(): void {
		const note = DeliveryComponent.buildDeliveryNoteLines(this.order.lines, this.qtiesmap, this.delivery_index);
		const deliveryFg = this.getFormGroupDelivery();   // livraison active (qtés livrées)
		this.logisticService.beforeSubmit(deliveryFg);    // prépare le payload comme onSubmit
		const dialogRef = this.dialog.open(DeliveryValidationComponent, {
			width: '850px',
			maxWidth: '95vw',
			data: {
				order: this.order,                        // commande (N° dans l'entête du BL)
				deliveryFormGroup: deliveryFg,            // livraison active postée depuis le dialog
				customer: this.order.lnk_customer,
				lines: note.lines,                         // lignes limitées aux qtés livrées
				amount_taxi: note.amount_taxi,             // total TTC recalculé sur les qtés livrées
				amount_taxx: note.amount_taxx,             // total HT recalculé sur les qtés livrées
			},
		});
		this.subscriptions.add(
			dialogRef.afterClosed().subscribe((response: any) => {
				if (response) {   // le dialog a validé côté serveur avec succès
					this.immediateDeliveryIssued = true;
					this.alertService.success("Livraison validée — Commande num " + response?.data?.id_order);
					this.reload(this.delivery_index);
				}
			})
		);
	}

	// BL de la livraison active : ne garde que les lignes réellement livrées (qty > 0) dans delivery_index. Pure => testable sans TestBed.
	// Montants du serveur sur la qté livrée : ligne = round(qté × unet, 2), éco-participation déjà dans unet ; pied = TVA arrondie UNE fois par taux (BG-23).
	// `bl_qty` = quantité signée du BL (−1 = reprise d'un matériel loué, flèche ←) ; `qty` = sa valeur absolue, imprimée ; une reprise ne vaut rien.
	static buildDeliveryNoteLines(orderLines: Sales_order_l[], qtiesmap: Map<number, HashElement> | undefined, delivery_index: number): { lines: any[], amount_taxi: number, amount_taxx: number } {
		const lines = (orderLines ?? [])
			.filter(line => !!line.product_name)
			.map(line => {
				const bl_qty = +(qtiesmap?.get(line.id)?.qties[delivery_index] ?? 0);
				const qty = Math.abs(bl_qty);
				const factor = 1 + (+line.vat_rate || 0) / 100;
				const net_taxx = bl_qty < 0 ? 0 : lineNetTaxx(qty, +(line.unet_taxx ?? line.uprice_taxx)); // unet absent = tarif ; 0 = remise de 100 %
				return { ...line, qty, bl_qty, net_taxx, net_taxi: roundHalfAway(net_taxx * factor, 2) };
			})
			.filter(line => line.qty > 0);
		const { amount_taxx, amount_taxi } = vatTotals(lines);
		return { lines, amount_taxi, amount_taxx };
	}


	loadDeliveryIndex() {
		if (!this.order?.lnk_deliveries?.length) return;
		// Clamp de l'index demandé sur les bornes réelles : un ?index= hors bornes retombe sur la dernière
		// livraison au lieu de laisser delivery_index = -1 (page sans colonne / UPDATE avec objectid indéfini).
		let index = Math.min(Math.max(this.param_index, 0), this.order.lnk_deliveries.length - 1);
		// Ouverture sans index demandé (barre d'action) : un bon signé cède la place au dernier bon ouvert.
		// Un index demandé (bon cliqué, onglet, ?index=) fait foi, même sur un bon signé.
		if (this.opening && this.isDelivered(this.order.lnk_deliveries[index])) {
			for (let i = this.order.lnk_deliveries.length - 1; i >= 0; i--) {
				if (!this.isDelivered(this.order.lnk_deliveries[i])) { index = i; break; }
			}
		}
		this.opening = false;
		this.delivery_index = index;
		this.formGroup = Delivery.asFormGroup(this.order.lnk_deliveries[index]);
		this.loadOrderEventsSubject.next(this.formGroup);
	}

	onAddDelivery(fromLoad = false) {	// fromLoad : création d'office au chargement, dont un refus doit quand même afficher les bons existants
		if (this.isSaving) return;	// garde anti double-clic : pas de double INSERT en parallèle
		this.isSaving = true;
		let newdelivery = new Delivery();
		newdelivery.id_order = this.id;
		newdelivery.mode = 1;
		let newfg = Delivery.asFormGroup(newdelivery);
		this.subscriptions.add(this.logisticService.postDeliveryEvent(constants.EVENTTYP_INSERT, newfg).pipe(this.spin.block('Création du bon de livraison…')).subscribe({
			next: (response: any) => {
				this.isSaving = false;
				// Refus métier du serveur (HTTP 200 + success:false — pièce scellée, cf. FiscalIntegrityError) :
				// on NE recharge PAS, sinon l'invariant « il reste à livrer ⇒ un bon ouvert » relancerait l'INSERT en boucle.
				if (response?.success === false) {
					this.alertService.error(response.msg || "Création de livraison refusée");
					if (fromLoad) this.loadDeliveryIndex();
					return;
				}
				this.reload(this.order.lnk_deliveries?.length ?? 0);
			},
			error: (_error) => {
				this.isSaving = false;
				this.alertService.error("ERREUR à la création de livraison");
				if (fromLoad) this.loadDeliveryIndex();
			}
		}));
	}

	// Imprime le BL de la livraison active — 2 comportements :
	// - Comportement 1 (BL NON signé) : « Planifier + imprimer le BL ». POST : le back met à jour la livraison
	//   (qtés en cours) et renvoie le BL rendu en HTML (comportement historique).
	// - Comportement 2 (BL signé) : « Imprimer le BL signé ». Lecture pure (GET) du PDF d'archivage signé,
	//   aucun POST, aucune écriture serveur.
	onPrintDelivery(): void {
		if (!this.ensureQtiesValid()) return;
		if (this.isDelivered(this.activeDelivery)) {
			this.printSignedBl();
			return;
		}

		if (this.isSaving) return;	// garde anti double-clic : pas de double sauvegarde + double impression en parallèle
		this.isSaving = true;

		// Comportement 1 — « Planifier + imprimer le BL » :
		// 1) sauvegarde COMPLÈTE de la livraison (entête planification : date/créneau/mode… + lignes/qtés)
		//    via l'event UPDATE (le back persiste tout, contrairement à l'event PRINT qui ne resync que les lignes),
		// 2) puis génération + impression du BL.
		const saveFg = this.getFormGroupDelivery();
		this.subscriptions.add(
			this.logisticService.postDeliveryEvent(constants.EVENTTYP_UPDATE, saveFg).pipe(this.spin.block('Enregistrement de la livraison…')).subscribe({
				next: () => {
					this.isSaving = false;
					this.changeWithoutSave = false;
					this.saveCoordsIfDirty(() => this.printDeliveryHtml());		// la snackbar attend que l'impression soit lancée
				},
				error: (error) => {
					this.isSaving = false;
					console.error('Error saving delivery before print:', error);
					this.alertService.error("ERREUR Serveur. La livraison n'a pas été enregistrée");
				}
			})
		);
	}

	// Génère le BL (rendu HTML par le back) de la livraison active et l'envoie à l'impression.
	private printDeliveryHtml(): void {
		const fg = this.getFormGroupDelivery();
		this.logisticService.beforeSubmit(fg);
		this.subscriptions.add(
			this.logisticService.postDeliveryPrintEvent$(fg).pipe(this.spin.block('Génération du bon de livraison…')).subscribe({
				next: (response: any) => {
					const data = response?.data;
					// PDF du serveur d'abord : lui seul porte le pied paginé (« Page 2 / 3 ») ; le HTML est le repli quand Gotenberg n'a pas répondu.
					if (data?.pdf_base64) {
						this.printPdfBase64(data.pdf_base64);
						this.announcePlanned(fg);		// l'iframe d'impression vit dans le body : le lien peut fermer la fenêtre sans l'interrompre
						return;
					}
					if (!data?.html) {
						console.error('No HTML content received from server');
						this.alertService.error("ERREUR lors de la génération du BL");
						return;
					}
					this.printHtml(data.html);
					this.announcePlanned(fg);
				},
				error: (error) => {
					console.error('Error in onPrintDelivery:', error);
					this.alertService.error("ERREUR lors de la génération du BL");
				}
			})
		);
	}

	// Comportement 2 — récupère le PDF d'archivage signé via GET pur (aucune écriture serveur) et l'imprime
	// en silencieux via un iframe caché. Comportement unique pour tous les navigateurs.
	private printSignedBl(): void {
		const deliveryId = this.activeDelivery?.id;
		if (!deliveryId) { this.alertService.error("ERREUR lors de la génération du BL"); return; }
		if (this.isSaving) return;	// Entrée sur « Imprimer » pendant le GET (le voile ne fige pas le clavier) : pas de seconde impression
		this.isSaving = true;

		this.subscriptions.add(
			this.logisticService.getSignedBl$(deliveryId).pipe(this.spin.block('Bon de livraison signé…')).subscribe({
				next: (blob: Blob) => {
					this.isSaving = false;
					this.printer.printPdfBlob(blob);
				},
				error: (error) => {
					this.isSaving = false;
					console.error('Error in printSignedBl:', error);
					this.alertService.error(error?.status === 404 ? "Aucun BL signé pour cette livraison" : "ERREUR lors de la génération du BL");
				}
			})
		);
	}

	// Iframe caché du bloc partagé DeliveryPrintService (même impression que la fenêtre de livraison location).
	private printPdfBase64(pdfBase64: string): void {
		this.printer.printPdfBase64(pdfBase64);
	}

	private printHtml(html: string): void {
		this.printer.printHtml(html);
	}

	onSubmit(new_index?: number) {
		if (!this.ensureQtiesValid()) return;
		if (this.isSaving) return;

		// Livraison active déjà signée (immutable) : le serveur refuserait un UPDATE.
		// On saute la sauvegarde et on change seulement l'index de livraison.
		if (this.isDelivered(this.activeDelivery)) {
			this.reload(new_index !== undefined ? new_index : this.delivery_index);
			return;
		}

		this.isSaving = true;

		let fg = this.getFormGroupDelivery();
		this.subscriptions.add(this.logisticService.postDeliveryEvent(constants.EVENTTYP_UPDATE, fg).pipe(this.spin.block('Enregistrement de la livraison…')).subscribe({
			next: (response: any) => {
				this.isSaving = false;
				this.changeWithoutSave = false;
				let new_index_message: number = +this.delivery_index + 1;
				this.alertService.success("Enregistrement OK: Commande num " + response.data.id_order + " Liv. " + new_index_message.toString() + "         #" + response.data.delivery_id);
				this.saveCoordsIfDirty(() => this.reload(new_index !== undefined ? new_index : this.delivery_index));
			},
			error: (e) => {
				this.isSaving = false;
				this.alertService.error("ERREUR Serveur. La livraison n'a pas été enregistrée");
				console.error(e.error);
			}
		}));
	}

	onDeleteDelivery(index: number) {
		const delivery = this.order.lnk_deliveries?.[index];
		if (!delivery) return;
		if (this.isDelivered(delivery)) {	// Une livraison signée est immutable : suppression interdite
			this.alertService.error("Impossible de supprimer une livraison signée");
			return;
		}
		// Action destructive (qtés saisies + planification perdues) : confirmation explicite avant l'event DELETE.
		Swal.fire({
			title: `Supprimer la livraison ${index + 1} ?`,
			text: "Les quantités saisies et la planification de ce bon de livraison seront définitivement perdues.",
			icon: 'warning',
			showCancelButton: true,
			confirmButtonText: 'Oui, supprimer',
			cancelButtonText: 'Annuler',
			willOpen: (popup) => liftToTopLayer(popup.parentElement),
		}).then((result) => {
			if (result.isConfirmed) this.deleteDelivery(index, delivery);
		});
	}

	private deleteDelivery(index: number, delivery: Delivery): void {
		const fg = Delivery.asFormGroup(delivery);
		this.subscriptions.add(this.logisticService.postDeliveryEvent(constants.EVENTTYP_DELETE, fg).pipe(this.spin.block('Suppression de la livraison…')).subscribe({
			next: () => {
				const newIndex = index <= this.delivery_index ? Math.max(0, this.delivery_index - 1) : this.delivery_index;
				this.reload(newIndex);
			},
			error: () => this.alertService.error("ERREUR à la suppression de la livraison")
		}));
	}

	onPlanificationChanged() {
		this.changeWithoutSave = true;
	}

	// « Planifier la livraison » : enregistre la livraison active puis la recharge en place, sans fermer la fenêtre.
	onPlanDelivery() {
		if (!this.ensureQtiesValid()) return;
		if (this.isSaving) return;
		if (this.isDelivered(this.activeDelivery)) return;	// BL signé : immutable (bouton désactivé, garde de cohérence)

		this.isSaving = true;
		const fg = this.getFormGroupDelivery();
		this.subscriptions.add(this.logisticService.postDeliveryEvent(constants.EVENTTYP_UPDATE, fg).pipe(this.spin.block('Enregistrement de la livraison…')).subscribe({
			next: () => {
				this.isSaving = false;
				this.changeWithoutSave = false;
				this.announcePlanned(fg);
				this.saveCoordsIfDirty(() => this.reload(this.delivery_index));
			},
			error: (e) => {
				this.isSaving = false;
				this.alertService.error("ERREUR Serveur. La livraison n'a pas été enregistrée");
				console.error(e.error);
			}
		}));
	}

	/** Ferme la modale en propageant l'indicateur d'émission de livraison immédiate. */
	closeDialog(): void {
		this.dialogRef?.close({ immediateDelivery: this.immediateDeliveryIssued });
	}

	// Snackbar des deux Planifier, émise une fois le travail fini (enregistrement, ou impression lancée) : son lien ne coupe rien en vol.
	private announcePlanned(fg: FormGroup): void {
		this.alertService.success("La planification a bien eu lieu", { action: this.planningLink(fg) });	// le serveur ne répond qu'après le commit
	}

	// « Voir au planning » de la snackbar : tournées du jour demandé, sur sa demi-journée (règle du board), BL en surbrillance (?bl=).
	// Nouvel onglet puis fermeture normale de la fenêtre : la commande reste derrière et fait son travail de fermeture (« Livré » après une signature).
	planningLink(fg: FormGroup): AlertAction | undefined {
		const id = Number(fg.get('id')?.value) || 0;
		const dat = fg.get('askdatdel')?.value;		// 'YYYY-MM-DD' : beforeSubmit l'a normalisée à l'envoi
		if ((Number(fg.get('mode')?.value) || 0) < constants.SALES_ORDER_MODE_ASAP_DELIVERY || !id || !dat) return undefined;
		const day = DelrunsService.slotDayOf(fg.get('askslot')?.value, this.deliverySlots);
		const seg = ['/logistic/delruns', dat, ...(day === 1 ? ['matin'] : day === 2 ? ['apres-midi'] : [])];
		const url = this.router.serializeUrl(this.router.createUrlTree(seg, { queryParams: { bl: id } }));
		return { label: 'Voir au planning', run: () => {
			if (!window.open(url, '_blank')) { this.alertService.error('Onglet bloqué par le navigateur — autorisez les pop-ups pour ce site'); return; }
			this.closeDialog();
		} };
	}

	// Garde commune des sorties (enregistrer / ajouter / imprimer / valider / changer de livraison) :
	// tant que la dernière quantité saisie est refusée, l'input affiche une valeur qui ne correspond
	// pas au modèle — on bloque pour ne jamais enregistrer « autre chose que ce que montre l'écran ».
	private ensureQtiesValid(): boolean {
		if (this.isQtyValidable) return true;
		this.alertService.error("Quantité en erreur : corrigez la saisie avant de continuer");
		return false;
	}

	onQtyBlur(line: any, event: FocusEvent): void {
		this.isQtyValidable = true;
		const inputElement = event.target as HTMLInputElement;
		const value = inputElement ? inputElement.value : 0;
		const newQty = value === '' ? NaN : Number(value);
		// Négatif = reprise d'un matériel loué (−1 sur sa ligne, JP 29/10) : jamais sur une autre ligne ; le serveur borne au posé.
		if (isNaN(newQty) || (newQty < 0 && line?.typ !== constants.OrderLineType.RENTAL)) {
			this.isQtyValidable = false;
			this.alertService.error(newQty < 0 ? "ERREUR qté négative : réservée à la reprise d'un matériel loué" : "ERREUR qté livrée invalide");
			setTimeout(() => { inputElement.focus(); }, 1);
			return;
		}

		if (!this.qtiesmap) { console.error('no qtiesmap'); return; }
		const element = this.qtiesmap.get(line.id);
		if (!element) { console.error('no element'); return; }
		let oldQty = element.qties[this.delivery_index];
		if (oldQty === undefined) { console.error('no oldQty, delivery_index: ', this.delivery_index); return; }
		oldQty = Number(oldQty);

		// Le reste à livrer ne compte que le posé : une reprise (−) ne le change pas.
		if (newQty > element.undelivered_qty + Math.max(oldQty, 0)) {
			this.isQtyValidable = false;
			this.alertService.error(`ERREUR qté saisie ${newQty} > reste à livrer ${element.undelivered_qty + Math.max(oldQty, 0)}`);
			setTimeout(() => { inputElement.focus(); }, 1);
			return;
		} else {
			element.qties[this.delivery_index] = newQty;
			element.undelivered_qty = element.undelivered_qty + Math.max(oldQty, 0) - Math.max(newQty, 0);
			this.changeWithoutSave = true
		}
	}

	// « Ajouter une livraison » : sauvegarde le BL actif (sauf s'il est signé/immutable) puis crée un nouveau BL.
	saveAndAddDelivery() {
		if (!this.ensureQtiesValid()) return;
		if (this.isSaving) return;

		// BL actif déjà signé (immutable) : le serveur refuserait un UPDATE.
		// On saute la sauvegarde et on demande directement la création d'un nouveau BL.
		if (this.isDelivered(this.activeDelivery)) {
			this.onAddDelivery();
			return;
		}

		this.isSaving = true;

		let fg = this.getFormGroupDelivery();
		this.subscriptions.add(this.logisticService.postDeliveryEvent(constants.EVENTTYP_UPDATE, fg).pipe(this.spin.block('Enregistrement de la livraison…')).subscribe({
			next: (response: any) => {
				this.isSaving = false;
				this.changeWithoutSave = false;
				this.alertService.success("Enregistrement OK: Commande num " + response.data.id_order + " Liv. " + (+this.delivery_index + 1).toString() + "         #" + response.data.delivery_id);
				this.saveCoordsIfDirty(() => this.onAddDelivery());
			},
			error: (e) => {
				this.isSaving = false;
				this.alertService.error("ERREUR Serveur. La livraison n'a pas été enregistrée");
				console.error(e.error);
			}
		}));
	}

	getFormGroupDelivery(): FormGroup {
		const rawValues = this.formGroup.getRawValue();
		const requestFg: FormGroup = new FormGroup({});
		for (const key of Object.keys(rawValues)) {
			if (key !== 'lnk_deliveryl') {
				requestFg.addControl(key, new FormControl(rawValues[key]));
			}
		}
		requestFg.addControl('lnk_deliveryl', this.fb.array([]));
		this.qtiesmap?.forEach((element: HashElement, key) => {
			let qty = element.qties[this.delivery_index];
			if (qty !== 0) {
				const fg = new FormGroup({
					id_delivery: new FormControl<number>(+rawValues['id']),	// id de la livraison active (pas de la commande)
					id_order_l: new FormControl<number>(+key),
					qty: new FormControl<number>(qty),
				});
				(requestFg.get('lnk_deliveryl') as FormArray).push(fg);
			}
		});
		return requestFg;
	}

	// Une livraison est "livrée/validée" dès qu'elle porte une realdatdel (date réelle de remise).
	// realdatdel arrive du serveur en ISO (timestamptz) ; null/0/'' tant que non livrée. Pure => testable sans TestBed.
	static isDeliveryDelivered(delivery: any): boolean {
		const raw = delivery?.realdatdel;
		if (raw === null || raw === undefined) return false;
		if (typeof raw === 'number') return raw !== 0;
		const s = String(raw).trim();
		return s !== '' && s !== '0';
	}

	// Wrapper instance pour le template
	isDelivered(delivery: any): boolean {
		return DeliveryComponent.isDeliveryDelivered(delivery);
	}

	// Bon d'annulation du reste à livrer (mode réservé, négatif) : réalisé dès sa création, ses quantités soldent la commande sans être livrées.
	isCancel(delivery: any): boolean {
		return (Number(delivery?.mode) || 0) < 0;
	}

	get hasCancel(): boolean {
		return (this.order?.lnk_deliveries ?? []).some((d: any) => this.isCancel(d));
	}

	// Livraison active (colonne éditable)
	get activeDelivery(): any {
		return this.order?.lnk_deliveries?.[this.delivery_index];
	}

	// Reste à livrer de la commande (0 = soldée), règle du serveur (insert_remaining_delivery_lines) : ligne par ligne, un reste ≤ 0 ne compte pas.
	get remainingQty(): number {
		let sum = 0;
		this.qtiesmap?.forEach((e) => { sum += Math.max(0, roundHalfAway(+e.undelivered_qty, 4)); });	// retour article négatif, résidu flottant (0,4 − 0,1 − 0,3 > 0)
		return sum;
	}

	// Tous les bons de la commande sont signés : plus rien n'est saisissable tant qu'on n'en ouvre pas un autre.
	get allDeliveriesSigned(): boolean {
		const ds = this.order?.lnk_deliveries ?? [];
		return ds.length > 0 && ds.every((d: any) => this.isDelivered(d));
	}

	// Colonne des flèches posée seulement si une ligne est louée (RENTAL) : une commande F2 garde sa grille.
	get hasRentalDirection(): boolean {
		return (this.order?.lines ?? []).some(line => rentalDirection(line.typ, line.qty) !== null);
	}

	// Ligne louée : l'intitulé de son contrat de location, l'article dessous, comme sur le BL imprimé.
	lineTitle(line: Sales_order_l): string { return rentalTitle(orderLineNamed(line)) ?? ''; }
	lineArticle(line: Sales_order_l): string | null { return rentalSubtitle(orderLineNamed(line)); }

	// Ligne louée à quantité négative (reprise hors contrat) : la ligne passe en rouge pâle ; une reprise sur BL se lit dans sa colonne (−1).
	isReprise(line: Sales_order_l): boolean {
		return rentalDirection(line?.typ, line?.qty) === 'reprise';
	}

	isRentalLine(line: Sales_order_l): boolean {
		return line?.typ === constants.OrderLineType.RENTAL;
	}

	ngOnDestroy(): void {
		this.subscriptions.unsubscribe();
		this.geo.destroy();
	}

	public get_qtiesmap(): Map<number, HashElement> {
		const hashmap: Map<number, HashElement> = new Map();
		this.order.lines.forEach(order_line => {
			// Une ancienne ligne RENTAL_RETURN n'est jamais à livrer : pose et reprise pointent la ligne louée (JP 30/10).
			const old = order_line.typ === constants.OrderLineType.RENTAL_RETURN;
			let element: HashElement = {
				qties: [],
				undelivered_qty: old ? 0 : +order_line.qty,
			};
			if (this.order.lnk_deliveries && this.order.lnk_deliveries.length > 0) for (let j: number = 0; j < this.order.lnk_deliveries.length; j++) {
				let del_line: DeliveryL | undefined;
				if ((this.order.lnk_deliveries[j].hasOwnProperty('lnk_deliveryl') && this.order.lnk_deliveries![j].lnk_deliveryl)) {
					del_line = this.order.lnk_deliveries[j].lnk_deliveryl.find((del_line) => del_line.id_order_l == order_line.id);
				}
				element.qties.push(del_line ? +del_line.qty : 0);
				if (!old) element.undelivered_qty -= (del_line ? Math.max(+del_line.qty, 0) : 0);	// une reprise (−1) ne remet rien à livrer
			}
			hashmap.set(order_line.id, element);
		});
		return hashmap;
	}

	get isDistinctBeneficiary(): boolean {
		const recipientId = this.order?.data?.recipient;
		return !!recipientId && +recipientId !== +this.order?.customer;
	}

	public beneficiary: any = null;

	private loadBeneficiary(): void {
		const recipientId = this.order?.data?.recipient;
		if (recipientId && +recipientId !== +this.order?.customer) {
			this.subscriptions.add(
				this.partnerService.getPartnerByid(recipientId).subscribe({
					next: (partners: any) => {
						this.beneficiary = Array.isArray(partners) ? partners[0] : partners;
					},
					error: (err: any) => console.error('[DeliveryComponent] Erreur chargement tiers bénéficiaire', err),
				})
			);
		} else {
			this.beneficiary = null;
		}
	}

	get beneficiary_label(): string {
		if (this.beneficiary) {
			const fn = (this.beneficiary.firstname ?? '').trim();
			const nm = (this.beneficiary.name ?? '').trim();
			const full = [fn, nm].filter((x) => !!x).join(' ');
			if (full) return full;
		}
		if (this.order?.lnk_partner_deliv) {
			const fn = (this.order.lnk_partner_deliv.firstname ?? '').trim();
			const nm = (this.order.lnk_partner_deliv.name ?? '').trim();
			const full = [fn, nm].filter((x) => !!x).join(' ');
			if (full) return full;
		}
		return '';
	}

	get customer_label(): string {
		let ret = "ERREUR : Client  non renseigné";
		let name = (this.order.lnk_customer && this.order.lnk_customer.name ? this.order.lnk_customer.name : '');
		let firstname = (this.order.lnk_customer && this.order.lnk_customer.firstname ? this.order.lnk_customer.firstname : '');
		if (firstname + name !== '') ret = firstname + ' ' + name;
		return ret;
	}

	get adr_delivery(): string {
		if (!this.order || !this.order.lnk_customer) return '';
		const adresses = Partner.addresses(this.order.lnk_customer);	// lignes de rôle -5 de c_partner_partner
		if (!adresses.length) return "Non renseignée";
		let a: any = adresses.find((a: any) => a.id == this.order.addr_deliv);
		if (a) {
			// street1 (N° et rue) en tête — même ordre que les tournées ; champs vides ignorés (jamais « null » à l'écran)
			return [a.street1, a.street3, a.street2, a.zip, a.city].filter((v: any) => !!v).join(' ');
		}
		return "ERREUR : Adresse de livraison non renseignée";
	}

	// ============================================================
	// Géolocalisation de l'adresse de livraison (cufs Latitude/Longitude)
	// ============================================================

	// Ligne d'adresse de livraison (c_partner rôle 4, rattachée par c_partner_partner) jointe à la commande
	get deliveryAddress(): any {
		return Partner.addresses(this.order?.lnk_customer).find((a: any) => a.id == this.order.addr_deliv) ?? null;
	}

	// Bloc partagé DeliveryGeo : coordonnées en base sinon géocodage BAN, persistées sur l'adresse client à la sauvegarde.
	private initGeo(): void {
		this.geo.init(this.deliveryAddress, this.order?.lnk_customer?.name, () => this.deliveryAddress);
	}

	onMapCoords(c: { lat: number; lon: number }): void {	// pin déplacé à la main sur la carte
		this.geo.moved(c);
	}

	private saveCoordsIfDirty(done: () => void): void {
		this.geo.saveIfDirty(this.order.customer, this.deliveryAddress, done);
	}
}

// La fenêtre (MatDialog, Angular 22) vit dans le top layer : sans cela la Swal est peinte DESSOUS. Même procédé que liftToTopLayer de partner.component.ts.
function liftToTopLayer(container: HTMLElement | null): void {
	if (!container || typeof container.showPopover !== 'function') return;
	container.setAttribute('popover', 'manual');
	Object.assign(container.style, { width: 'auto', margin: '0', border: '0', color: 'inherit' });
	try { container.showPopover(); } catch (e) { console.warn('[DeliveryComponent] showPopover refusé', e); }
}
